'use client';

import { useRef } from 'react';
import { useInView, type UseInViewOptions } from 'motion/react';
import { useInstantMotion } from '@/hooks/use-instant-motion';

type ViewportMargin = NonNullable<UseInViewOptions>['margin'];

export type ScrollRevealMotionOptions = {
  margin?: ViewportMargin;
  amount?: number;
};

const instantRevealProps = {
  initial: false as const,
  animate: 'visible' as const,
};

/** Motion props for scroll-triggered section reveals (whileInView). */
export function useScrollRevealMotion(options?: ScrollRevealMotionOptions) {
  const instantMotion = useInstantMotion();

  if (instantMotion) {
    return instantRevealProps;
  }

  return {
    initial: 'hidden' as const,
    whileInView: 'visible' as const,
    viewport: {
      once: true,
      amount: options?.amount ?? 0,
      ...(options?.margin !== undefined ? { margin: options.margin } : {}),
    },
  };
}

/** Ref + animate props for sections that drive reveal via useInView (e.g. Services). */
export function useScrollRevealInView<T extends HTMLElement = HTMLDivElement>(
  options?: ScrollRevealMotionOptions,
) {
  const instantMotion = useInstantMotion();
  const ref = useRef<T>(null);
  const isInView = useInView(ref, {
    once: true,
    amount: options?.amount ?? 0,
    ...(options?.margin !== undefined ? { margin: options.margin } : {}),
  });

  if (instantMotion) {
    return {
      ref,
      ...instantRevealProps,
    };
  }

  return {
    ref,
    initial: 'hidden' as const,
    animate: isInView ? ('visible' as const) : ('hidden' as const),
  };
}
